/**
 * CustomerWaivers — list page for AISC §1.7.0 Phase 4.
 */

import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, Breadcrumbs, Link as MuiLink,
} from "@mui/material";
import { HomeRounded, AddRounded, WarningAmberRounded, AssignmentLateOutlined } from "@mui/icons-material";

import { listWaivers, WAIVER_STATUSES } from "../../utils/customerWaiversApi";
import EmptyState from "../../components/EmptyState/EmptyState";
import { fmtDateISO } from "../../utils/dateFmt";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLOR = {
  REQUESTED: { bg: "#fff3cd", color: "#664d03" },
  RECEIVED:  { bg: "#cfe2ff", color: "#052c65" },
  ACTIVE:    { bg: "#d1e7dd", color: "#0a3622" },
  REVOKED:   { bg: "#f8d7da", color: "#58151c" },
};

export default function CustomerWaivers() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const jobIdParam = params.get("job_id") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setNotAvailable(false);
    const filters = {};
    if (jobIdParam) filters.job_id = jobIdParam;
    if (statusFilter && statusFilter !== "ALL") filters.status = statusFilter;
    listWaivers(filters)
      .then((data) => { if (!cancelled) setRows((data && data.rows) || []); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404 || status === 501) setNotAvailable(true);
        else setError(err?.response?.data?.error || err?.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobIdParam, statusFilter]);

  return (
    <>
      <Helmet><title>Customer Waivers | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}><HomeRounded fontSize="small" /> <span>Home</span></Stack>
          </MuiLink>
          <Typography color="text.primary">Customer Waivers</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Customer Waivers</Typography>
          <Button variant="contained" startIcon={<AddRounded />}
            onClick={() => navigate(jobIdParam ? `/customer-waivers/new?job_id=${jobIdParam}` : "/customer-waivers/new")}>
            New Waiver Request
          </Button>
        </Stack>
        <PageGuide
          why="Document a customer's written decision to deviate from the approved drawing — so the deviation is authorized, not assumed."
          who="The QM and PM, recording a customer's authorized deviation."
          what="A record of each customer-approved waiver against a drawing or job."
          how="Request a waiver, capture the customer's approval, then activate it — which flags the linked job."
          when="When the customer agrees to a deviation from the approved detailing, before you fabricate to it."
          standard="AISC §1.7.0 §3.2"
        />

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField select label="Status" size="small" sx={{ minWidth: 200 }}
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="ALL">All statuses</MenuItem>
            {WAIVER_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Paper>

        {notAvailable && <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 2 }}>Phase 4 backend deploy hasn't landed.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          <SmartTable tableId="customer-waivers" lockedColumns={["ID", "Status"]}>
            <TableHead><TableRow>
              <TableCell>ID</TableCell><TableCell>Job</TableCell><TableCell>Scope</TableCell>
              <TableCell>Effective</TableCell><TableCell>Status</TableCell><TableCell>Created</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "#6c757d" }}>Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && !notAvailable && !error && (
                <TableRow><TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                  <EmptyState
                    icon={AssignmentLateOutlined}
                    title="No waiver requests yet"
                    description="Click New Waiver Request to formally record a customer's waiver of drawing approval. Tracks the lifecycle through REQUESTED, RECEIVED, ACTIVE, and REVOKED with audit-defensible evidence."
                    variant="plain"
                  />
                </TableCell></TableRow>
              )}
              {!loading && rows.map((w) => {
                const colors = STATUS_COLOR[w.status] || { bg: "#e9ecef", color: "#495057" };
                const eff = w.effective_from ? `${w.effective_from}${w.effective_until ? ` → ${w.effective_until}` : ""}` : "—";
                return (
                  <TableRow key={w.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/customer-waivers/${w.id}`)}>
                    <TableCell><strong>#{w.id}</strong></TableCell>
                    <TableCell>Job #{w.job_id}</TableCell>
                    <TableCell>{w.scope}</TableCell>
                    <TableCell>{eff}</TableCell>
                    <TableCell><Chip size="small" label={prettyStatus(w.status)}
                      sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600, fontSize: "0.7rem" }} />
                    </TableCell>
                    <TableCell>{fmtDateISO(w.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Container>
    </>
  );
}
